import React from "react";
import {
    Zap,
    BarChart2,
    Smile,
    FileText,
    Code,
    Clock,
    Calendar
} from "react-feather";
import { IUserCredits, ICreditInfo } from "../../../interfaces/user";
import { formatDate, normalizeTimestamp } from "../../../utils/date";
import "../../../assets/scss/components/_usage-credits-card.scss";

interface IProps {
    credits?: IUserCredits;
    title?: string;
    daysLeft?: number;
    isTrial?: boolean;
    onUpgradeClick?: () => void;
    onViewDetailsClick?: () => void;
    loading?: boolean;
}

const UsageCreditsCard: React.FC<IProps> = ({
    credits,
    title = "Usage & Credits",
    daysLeft,
    isTrial,
    onUpgradeClick,
    onViewDetailsClick,
    loading
}) => {
    const getIcon = (type: string) => {
        const key = (type || "").toLowerCase().trim();
        switch (key) {
            case "ingredient_searches": return <Zap size={16} className="usage-icon searches" />;
            case "nutrition_analyses": return <BarChart2 size={16} className="usage-icon analysis" />;
            case "ai_recommendations": return <Smile size={16} className="usage-icon ai" />;
            case "report_export":
            case "report_exports":
            case "report exports":
            case "reports": return <FileText size={16} className="usage-icon report-export" />;
            case "api_calls": return <Code size={16} className="usage-icon api" />;
            default: return <Zap size={16} className="usage-icon" />;
        }
    };

    const formatLabel = (key: string) => {
        return key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const renderUsageItem = (key: string, info: ICreditInfo) => {
        const limitText = info.limit === -1 ? "Unlimited" : info.limit;
        const percentage = info.limit === -1 ? 0 : (info.used / info.limit) * 100;

        return (
            <div key={key} className="usage-item">
                <div className="usage-item__info">
                    <div className="label-group">
                        {getIcon(key)}
                        <span className="usage-item__label">{formatLabel(key)}</span>
                    </div>
                    <span className="usage-item__value">
                        {info.used} / {limitText}
                    </span>
                </div>
                <div className="progress-container">
                    <div
                        className="progress-bar-fill"
                        style={{
                            width: `${Math.min(percentage, 100)}%`,
                            backgroundColor: "#0ed7ac"
                        }}
                    />
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="usage-credits-card loading-skeleton" style={{ height: "400px" }}></div>;
    }

    if (!credits) return null;

    return (
        <div className="usage-credits-card">
            <div className="usage-credits-card__header">
                <div className="title-group">
                    <h3>{title}</h3>
                    <div className="badge-group">
                        {isTrial && <span className="badge-trial">Free Trial</span>}
                        {daysLeft !== undefined && (
                            <span className="badge-days">
                                <Clock size={12} /> {daysLeft} days left
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {credits.billing_cycle_start && credits.billing_cycle_end && (
                <div className="usage-credits-card__billing-cycle">
                    <Calendar size={14} />
                    <span>Billing Cycle: </span>
                    <span className="date-range">
                        {formatDate(normalizeTimestamp(credits.billing_cycle_start))} - {formatDate(normalizeTimestamp(credits.billing_cycle_end))}
                    </span>
                </div>
            )}

            <div className="usage-credits-card__content">
                {Object.entries(credits.credits).map(([key, info]) => renderUsageItem(key, info))}
            </div>

            <div className="usage-credits-card__footer">
                <button className="btn-upgrade" onClick={onUpgradeClick}>Upgrade Plan</button>
                <button className="btn-details" onClick={onViewDetailsClick}>View Details</button>
            </div>
        </div>
    );
};

export default UsageCreditsCard;
